import React, { useCallback } from 'react';
import { FileDown } from 'lucide-react';
import { BaseImportExportButtons } from './BaseImportExportButtons';
import { parseExcelFile, exportToExcel, transformToAPIFormat } from '../../utils/excelUtils';
import * as XLSX from 'xlsx';

export function ImportExportButtons({ farmers, onImport, userRole }) {
  const getMunicipality = useCallback(
    (f) => f.municipality || f.address?.split(',').pop()?.trim() || null,
    []
  );

  const handleFileImport = useCallback(
    async (file) => {
      // Validate file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'application/csv',
      ];
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

      if (!validTypes.includes(file.type) && !hasValidExtension) {
        return { type: 'error', message: 'Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file' };
      }

      const parsedFarmers = await parseExcelFile(file);
      if (parsedFarmers.length === 0) {
        return { type: 'error', message: 'No valid farmer data found in the file' };
      }

      const transformedFarmers = parsedFarmers.map((farmer) => transformToAPIFormat(farmer));
      const result = await onImport(transformedFarmers);

      if (result && typeof result === 'object') {
        const { successCount, failCount, errors } = result;
        if (failCount === 0) {
          return { type: 'success', message: `Successfully imported all ${successCount} farmer(s)` };
        } else if (successCount === 0) {
          return {
            type: 'error',
            message: `Failed to import all ${failCount} farmer(s). ${errors ? errors.slice(0, 3).join('; ') : ''}`,
          };
        }
        return {
          type: 'success',
          message: `Imported ${successCount} farmer(s), ${failCount} failed. Check console for details.`,
        };
      }
      return { type: 'success', message: `Successfully imported ${transformedFarmers.length} farmer(s)` };
    },
    [onImport]
  );

  const handleDownloadTemplate = useCallback(() => {
    const templateData = [
      {
        'First Name': 'Juan', 'Last Name': 'Dela Cruz', 'Age': 45, 'Gender': 'Male',
        'Municipality': 'Tagbilaran City', 'Barangay': 'Poblacion', 'Phone': '09123456789',
        'Farm Size (hectares)': 2.5, 'Land Ownership': 'Owned', 'Years Farming': 15,
        'Cacao Varieties': 'UF18, BR25', 'Production (kg/year)': 500,
        'Training Attended': 'Yes', 'Certification': 'Organic',
      },
      {
        'First Name': 'Maria', 'Last Name': 'Santos', 'Age': 38, 'Gender': 'Female',
        'Municipality': 'Carmen', 'Barangay': 'San Jose', 'Phone': '09187654321',
        'Farm Size (hectares)': 1.8, 'Land Ownership': 'Inherited', 'Years Farming': 10,
        'Cacao Varieties': 'UF18', 'Production (kg/year)': 350,
        'Training Attended': 'Yes', 'Certification': 'None',
      },
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 8 }, { wch: 10 }, { wch: 20 },
      { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 18 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Farmers Template');
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `farmers_template_${timestamp}.xlsx`);
  }, []);

  return (
    <BaseImportExportButtons
      entityName="farmer"
      data={farmers}
      userRole={userRole}
      getMunicipality={getMunicipality}
      onFileChange={handleFileImport}
      onExport={exportToExcel}
      onDownloadTemplate={handleDownloadTemplate}
      accentColor="green"
      templateButtonIcon={FileDown}
      infoText="Import complete farmer data including personal info, farm details, cacao varieties, production capacity, and training records. Download the template first to see the required format and example data."
    />
  );
}

